// pages/findings/index.js
// ════════════════════════════════════════════════════════════════════
// LAB FINDINGS — corpus index (Phase F8)
//
// Lists every published finding in the registry, grouped by domain.
// Surfaces: title, lede description, form, lifecycle, visibility,
// sample size, citation handle. Each card links to the bare-slug
// canonical URL (which redirects to the latest versioned URL).
//
// Static-rendered from FINDING_REGISTRY at build time.
// ════════════════════════════════════════════════════════════════════

import Head from 'next/head';
import Link from 'next/link';
import { listFindings } from '../../features/findings/registry';
import { predictionStats } from '../../features/findings/predictions-registry';

const DOMAIN_LABELS = {
  strategic: 'Strategic Decisions',
  popdec:    'Population Decline',
};

const DOMAIN_ORDER = ['popdec', 'strategic'];

function lifecycleTone(lifecycle) {
  if (lifecycle === 'published')      return 'sage';
  if (lifecycle === 'pre-registered') return 'brass';
  if (lifecycle === 'draft')          return 'neutral';
  if (lifecycle === 'revised')        return 'cognac';
  if (lifecycle === 'retracted')      return 'oxblood';
  return 'neutral';
}

function visibilityTone(visibility) {
  if (visibility === 'private')        return 'neutral';
  if (visibility === 'shared-link')    return 'brass';
  if (visibility === 'public-peek')    return 'sage';
  if (visibility === 'public-collab')  return 'plum';
  return 'neutral';
}

function humanizeLifecycle(l) {
  if (l === 'pre-registered') return 'Pre-registered';
  if (l === 'published')      return 'Published';
  if (l === 'draft')          return 'Draft';
  if (l === 'revised')        return 'Revised';
  if (l === 'retracted')      return 'Retracted';
  return l;
}

function humanizeVisibility(v) {
  if (v === 'private')       return 'Private';
  if (v === 'shared-link')   return 'Shared by link';
  if (v === 'public-peek')   return 'Public · read-only';
  if (v === 'public-collab') return 'Public · collab';
  return v;
}

export async function getStaticProps() {
  const findings = listFindings();
  const predStats = predictionStats();
  return { props: { findings, predStats } };
}

export default function FindingsIndexPage({ findings, predStats }) {
  const byDomain = {};
  for (const f of findings) {
    if (!byDomain[f.domain]) byDomain[f.domain] = [];
    byDomain[f.domain].push(f);
  }

  const domains = [
    ...DOMAIN_ORDER.filter((d) => byDomain[d]),
    ...Object.keys(byDomain).filter((d) => !DOMAIN_ORDER.includes(d)),
  ];

  const totals = {
    findings: findings.length,
    domains: domains.length,
    preregistered: findings.filter((f) => f.lifecycle === 'pre-registered').length,
    published: findings.filter((f) => f.lifecycle === 'published').length,
  };

  return (
    <div className="sp-root">
      <Head>
        <title>Lab Findings · Shift</title>
        <meta
          name="description"
          content="Citable research findings from the Lab. Pre-registered predictions, structural analyses, and counterfactual investigations."
        />
      </Head>

      <div className="sp-page">
        <div className="findings-index-topbar">
          <Link href="/" className="finding-back">← Shift</Link>
          <div className="findings-index-topbar-meta">
            Lab · Findings index
          </div>
        </div>

        <header className="findings-index-header">
          <div className="finding-eyebrow">§ Lab Findings</div>
          <h1 className="findings-index-title">The corpus.</h1>
          <p className="findings-index-sub">
            Every Lab finding is pre-registered, version-pinned, and citable.
            Citation handle format: <code className="findings-index-handle-fmt">lab:finding/&lt;domain&gt;/&lt;year&gt;/&lt;slug&gt;/&lt;version&gt;</code>.
            New here? <Link href="/lab/methodology" className="methodology-inline-link">Read the methodology</Link> first.
          </p>
        </header>

        <section className="findings-index-stats">
          <Stat label="Findings" value={totals.findings} />
          <Stat label="Domains" value={totals.domains} />
          <Stat label="Pre-registered" value={totals.preregistered} />
          <Stat label="Published" value={totals.published} />
        </section>

        <div className="findings-index-cta-row">
          <Link href="/lab/predictions" className="findings-index-calendar-cta">
            <div className="findings-index-calendar-cta-eyebrow">Public falsifier register</div>
            <div className="findings-index-calendar-cta-row">
              <div>
                <div className="findings-index-calendar-cta-title">
                  Pre-registration calendar
                </div>
                <div className="findings-index-calendar-cta-sub">
                  {predStats.total} predictions &middot; {predStats.open} open &middot; {predStats.held + predStats.overturned + predStats.partial} resolved
                </div>
              </div>
              <div className="findings-index-calendar-cta-arrow">→</div>
            </div>
          </Link>

          <Link href="/lab/briefs" className="findings-index-calendar-cta">
            <div className="findings-index-calendar-cta-eyebrow">Weekly cadence</div>
            <div className="findings-index-calendar-cta-row">
              <div>
                <div className="findings-index-calendar-cta-title">
                  Weekly Briefs
                </div>
                <div className="findings-index-calendar-cta-sub">
                  Cross-corpus synthesis &middot; published Sundays
                </div>
              </div>
              <div className="findings-index-calendar-cta-arrow">→</div>
            </div>
          </Link>
        </div>

        {domains.map((domain) => (
          <section key={domain} className="findings-index-domain">
            <div className="findings-index-domain-head">
              <div className="findings-index-domain-eyebrow">§ Domain</div>
              <h2 className="findings-index-domain-title">
                {DOMAIN_LABELS[domain] || domain}
              </h2>
              <div className="findings-index-domain-count">
                {byDomain[domain].length} {byDomain[domain].length === 1 ? 'finding' : 'findings'}
              </div>
            </div>

            <div className="findings-index-grid">
              {byDomain[domain].map((f) => (
                <FindingCard key={`${f.domain}/${f.year}/${f.slug}/${f.version}`} f={f} />
              ))}
            </div>
          </section>
        ))}

        <footer className="findings-index-footer">
          <p>
            Lab findings ship under a research-first model: every claim
            is pre-registered with a public falsifier, every version is
            preserved, every retraction is visible. The corpus grows by
            authored work, not generated content.
          </p>
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="findings-index-stat">
      <div className="findings-index-stat-value">{value}</div>
      <div className="findings-index-stat-label">{label}</div>
    </div>
  );
}

function FindingCard({ f }) {
  const lcTone = lifecycleTone(f.lifecycle);
  const vTone = visibilityTone(f.visibility);
  return (
    <Link href={f.path} className="findings-index-card">
      <div className="findings-index-card-form">
        Form {f.form} · {f.formName}
      </div>
      <h3 className="findings-index-card-title">{f.title}</h3>
      {f.description && (
        <p className="findings-index-card-desc">{f.description}</p>
      )}
      <div className="findings-index-card-pills">
        <span className={`finding-version-badge finding-version-badge-${lcTone}`}>
          {f.version} · {humanizeLifecycle(f.lifecycle)}
        </span>
        {f.visibility && (
          <span className={`finding-visibility-badge finding-visibility-badge-${vTone}`}>
            {humanizeVisibility(f.visibility)}
          </span>
        )}
        {f.sampleSize > 0 && (
          <span className="findings-index-card-pill-n">N={f.sampleSize}</span>
        )}
      </div>
      <div className="findings-index-card-handle">
        <code>{f.citationHandle}</code>
      </div>
    </Link>
  );
}
